/*
  Desktop mega menu behaviour layered on top of <header-menu> (details-disclosure.js):
  - opens on hover for mouse users, one panel at a time
  - stays open while the pointer is inside the header, closes when it leaves
  - Escape closes; ArrowDown/ArrowUp move through the open panel's links
*/
if (!customElements.get('mega-menu-nav')) {
  class MegaMenuNav extends HTMLElement {
    connectedCallback() {
      this.menus = Array.from(this.querySelectorAll('details.mega-menu'));
      this.header = this.closest('.site-header');
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.menus.forEach((details) => {
        const summary = details.querySelector('summary');
        details.parentElement.addEventListener('mouseenter', () => this.onEnter(details));
        summary.addEventListener('click', (event) => this.onSummaryClick(event, details));
        details.addEventListener('toggle', () => this.syncExpanded(details));
        details.addEventListener('keydown', (event) => this.onKeydown(event, details));
      });

      this.querySelectorAll('.mega-nav__entry > a').forEach((link) => {
        link.addEventListener('mouseenter', () => this.canHover.matches && this.closeAll());
      });

      this.onHeaderLeave = () => this.canHover.matches && this.closeAll();
      this.header?.addEventListener('mouseleave', this.onHeaderLeave);
    }

    disconnectedCallback() {
      this.header?.removeEventListener('mouseleave', this.onHeaderLeave);
    }

    onEnter(details) {
      if (!this.canHover.matches) return;
      this.open(details);
    }

    onSummaryClick(event, details) {
      // Keyboard activation (event.detail === 0) keeps the native toggle.
      if (event.detail === 0) {
        this.menus.forEach((other) => other !== details && this.close(other));
        return;
      }

      // Items with a menu link go there: on click for mouse users (the panel opened on hover),
      // on a second tap for touch users (the first tap opens the panel).
      const href = event.currentTarget.dataset.href;
      if (href && (this.canHover.matches || details.open)) {
        event.preventDefault();
        window.location.href = href;
        return;
      }

      // Mouse users already opened the panel on hover; a click shouldn't toggle it shut.
      if (this.canHover.matches && details.open) event.preventDefault();
    }

    onKeydown(event, details) {
      if (event.key === 'Escape' && details.open) {
        this.close(details);
        details.querySelector('summary').focus();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = Array.from(details.querySelectorAll('.mega-menu__content a'));
      if (!links.length) return;

      event.preventDefault();
      if (!details.open) this.open(details);

      const index = links.indexOf(document.activeElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = index === -1 ? 0 : (index + step + links.length) % links.length;
      links[next].focus();
    }

    open(details) {
      this.menus.forEach((other) => other !== details && this.close(other));
      details.open = true;
    }

    close(details) {
      details.open = false;
    }

    closeAll() {
      this.menus.forEach((details) => this.close(details));
    }

    syncExpanded(details) {
      details.querySelector('summary').setAttribute('aria-expanded', details.open);
    }
  }

  customElements.define('mega-menu-nav', MegaMenuNav);
}
